import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React from 'react';

import { getPageNameLabel } from './myReportsListHelpers';
import { MyReportsPanel } from './MyReportsPanel';
import { MyReport } from './MyReportsTable.types';
import { useMyReportsListData } from './useMyReportsListData';

export interface ReportsModalProps {
  open: boolean;
  onClose: () => void;
  /** Report-type key of the host page, e.g. 'ServiceList'. Locks the list to it. */
  pageName: string;
  /** Applies the chosen report on the host page. The modal closes itself after. */
  onSelectReport: (code: string) => void;
}

/**
 * The full catalogue of saved reports for one list page, opened from that page's
 * Reports button.
 *
 * The list is pinned to the host page's report type: offering another page's
 * reports here would be a dead end, since picking one could only navigate away
 * from the page the user is working on.
 */
export const ReportsModal: React.FC<ReportsModalProps> = ({
  open,
  onClose,
  pageName,
  onSelectReport,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth={false}
    aria-labelledby="reports-modal-title"
    slotProps={{ paper: { sx: { width: '92vw', maxWidth: 1400, height: '86vh' } } }}
  >
    {/* Children mount only while open (MUI unmounts them on close), so each
        opening fetches a fresh list rather than showing a stale one. */}
    <ReportsModalBody pageName={pageName} onClose={onClose} onSelectReport={onSelectReport} />
  </Dialog>
);

const ReportsModalBody: React.FC<Omit<ReportsModalProps, 'open'>> = ({
  pageName,
  onClose,
  onSelectReport,
}) => {
  const data = useMyReportsListData({ lockedPageName: pageName });

  const handleSelect = (row: MyReport) => {
    onSelectReport(row.code);
    onClose();
  };

  return (
    <>
      <DialogTitle
        id="reports-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'v4.border',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography component="span" sx={{ fontSize: '16px', fontWeight: 700 }}>
            Reports
          </Typography>
          <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>
            {getPageNameLabel(pageName)}
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Close reports" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 0, pt: 2 }}>
        <MyReportsPanel data={data} onSelectReport={handleSelect} />
      </DialogContent>
    </>
  );
};
